import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiGrid,
  FiBookOpen,
  FiBell,
  FiPlus,
  FiUser,
  FiChevronDown,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiMenu,
  FiX,
} from "react-icons/fi";
import { HiOutlineSwitchVertical } from "react-icons/hi";
import logoName from "../../images/auravit_logo.png";
import auravitIcon from "../../images/auraviticon.png";
import cfsiLogo from "../../images/CFSI-mobile-logo-sr.png";

import ContentDashboard from "./ContentDashboard";
import ContentLibrary from "./ContentLibrary";
import ContentAnnouncements from "./ContentAnnouncements";
import ContentProfile from "./ContentProfile";
import StaffLogoutModal from "./StaffLogoutModal";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/contentcurator.css";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { switchRoleWithUpdate, roleConfigs } from "../../utils/roleSwitching";
import StaffChangePassword from "../../components/staffcomponents/StaffChangePassword";
import StaffChangeVerificationModal from "../../components/otpmodals/StaffChangeVerificationModal";

/* =========================
   Navigation Configuration
========================= */

const ContentCurator = () => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, true);
  
  const NAV_CONFIG = {
    dashboard: {
      sidebarLabel: t.dashboard,
      topTitle: t.dashboard
    },
    library: {
      sidebarLabel: t.resourceLibrary,
      topTitle: t.resourceLibrary
    },
    announcements: {
      sidebarLabel: t.announcements,
      topTitle: t.announcements
    },
    profile: {
      sidebarLabel: t.profile,
      topTitle: t.profile
    },
    "add-content": {
      sidebarLabel: t.createNewContent,
      topTitle: t.createNewContent
    }
  };
  
  const navigate = useNavigate();
  // Initialize activePanel from URL query param if available
  const getInitialActivePanel = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    if (viewParam && ['dashboard', 'library', 'announcements', 'profile', 'add-content'].includes(viewParam)) {
      return viewParam;
    }
    return "dashboard";
  };

  const [activePanel, setActivePanel] = useState(getInitialActivePanel);
  const [editingDraft, setEditingDraft] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isAnnouncementMode, setIsAnnouncementMode] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  
  // Onboarding state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showPasswordVerificationModal, setShowPasswordVerificationModal] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState(null);
  const [userData, setUserData] = useState(null);
  
  // Switch account dropdown state
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const switchButtonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (switchButtonRef.current && !switchButtonRef.current.contains(event.target) && 
          !event.target.closest('.cc-sidebar-role-dropdown')) {
        setShowRoleDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Effect to update URL when activePanel changes
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('view', activePanel);
    window.history.replaceState({}, '', url.toString());
  }, [activePanel]);

  // Desktop detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tooltip handlers
  const handleMouseEnter = (e, text) => {
    if (isDesktop && sidebarCollapsed) {
      const tooltipX = e.clientX + 12;
      const tooltipY = e.clientY;
      
      // Prevent tooltip from causing scroll by ensuring it stays within viewport
      const tooltipHeight = 40; // estimated height
      const viewportHeight = window.innerHeight;
      const adjustedY = Math.min(tooltipY, viewportHeight - tooltipHeight - 10);
      
      setTooltip({
        visible: true,
        text: text,
        x: tooltipX,
        y: adjustedY
      });
    }
  };

  const handleMouseMove = (e) => {
    if (tooltip.visible && isDesktop && sidebarCollapsed) {
      const tooltipX = e.clientX + 12;
      const tooltipY = e.clientY;
      
      // Prevent tooltip from causing scroll by ensuring it stays within viewport
      const tooltipHeight = 40; // estimated height
      const viewportHeight = window.innerHeight;
      const adjustedY = Math.min(tooltipY, viewportHeight - tooltipHeight - 10);
      
      setTooltip(prev => ({
        ...prev,
        x: tooltipX,
        y: adjustedY
      }));
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0 });
  };

  // Function to handle Create New - switches to add-content view
  const handleCreateNew = (isAnnouncement = false) => {
    setIsAnnouncementMode(isAnnouncement);
    setActivePanel("add-content");
  };

  // Function to handle back navigation from add-content
  const handleBack = () => {
    setActivePanel("dashboard");
    setEditingDraft(null);
    setIsAnnouncementMode(false);
  };

  // Function to handle editing drafts
  const handleEditDraft = (draft) => {
    setEditingDraft(draft);
    setActivePanel("add-content");
  };

  // Handle role switching
  const handleRoleSwitch = (selectedRole) => {
    const switched = switchRoleWithUpdate(selectedRole.id, currentUserRole, setCurrentUserRole, navigate);
    if (switched) {
      setShowRoleDropdown(false);
    }
  };

  // Calculate dropdown position for collapsed sidebar
  const handleDropdownToggle = () => {
    if (!showRoleDropdown && sidebarCollapsed && isDesktop && switchButtonRef.current) {
      const rect = switchButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 200; // estimated width
      const dropdownHeight = 150; // estimated height
      
      // Calculate position to center dropdown above button
      let top = rect.top - dropdownHeight - 8;
      let left = rect.left + (rect.width / 2) - (dropdownWidth / 2);
      
      // Ensure dropdown doesn't go off-screen horizontally
      if (left < 10) left = 10;
      if (left + dropdownWidth > window.innerWidth - 10) {
        left = window.innerWidth - dropdownWidth - 10;
      }
      
      // Ensure dropdown doesn't go off-screen vertically
      if (top < 10) top = rect.bottom + 8; // Show below if above would be off-screen
      
      setDropdownPosition({ top, left });
    }
    setShowRoleDropdown(!showRoleDropdown);
  };

  const handlePasswordVerificationRequired = (passwordData) => {
    setPendingPasswordChange(passwordData);
    setShowPasswordChangeModal(false); // Close password change modal
    setShowPasswordVerificationModal(true);
  };

  const handlePasswordVerificationSuccess = async (result) => {
    setShowPasswordVerificationModal(false);
    // Password change was successful, user will be signed out by the modal
    // Navigate to login page
    navigate('/login');
  };

  const handlePasswordVerificationCancel = () => {
    setShowPasswordVerificationModal(false);
    setShowPasswordChangeModal(true); // Reopen password change modal
  };
  
  // Listen for authentication changes - consolidated to prevent duplicate reads
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 5;
    const INITIAL_RETRY_DELAY = 1000; // 1 second
    
    const fetchUserRoles = async (user, retryDelay = INITIAL_RETRY_DELAY) => {
      if (!isMounted) return;
      
      try {
        if (!user) {
          if (retryCount < MAX_RETRIES) {
            retryCount++;
            // Exponential backoff: double the delay each time
            setTimeout(() => {
              if (isMounted) {
                const currentUser = auth.currentUser;
                fetchUserRoles(currentUser, retryDelay * 2);
              }
            }, retryDelay);
          } else {
            console.error('Max retries reached. User authentication failed to initialize.');
          }
          return;
        }
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!isMounted) return;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let roles = userData.roles || [];

          // If user is a Peer Educator and servicesAssigned is true, add Medical Professional to the role list
          if (roles.includes("Peer Educator") && 
              (userData.servicesAssigned === true || userData.servicesassigned === true) && 
              !roles.includes("Medical Professional")) {
            roles = [...roles, "Medical Professional"];
          }

          setUserRoles(roles);
          setUserData(userData);
          
          // Use centralized role configurations
          
          // Filter available roles based on user's assigned roles
          const filteredRoles = roles
            .filter(roleId => roleConfigs[roleId])
            .map(roleId => ({
              id: roleId,
              name: roleConfigs[roleId].name,
              component: roleConfigs[roleId].component
            }));
          
          setAvailableRoles(filteredRoles);
          
          // Set current role based on current page or first available role
          const currentPath = window.location.pathname;
          let currentRole = "";
          
          if (currentPath.includes("/medical-professional")) {
            currentRole = "Medical Professional";
          } else if (currentPath.includes("/peer-educator")) {
            currentRole = "Peer Educator";
          } else if (currentPath.includes("/content-curator")) {
            currentRole = "Content Curator";
          } else {
            // Default to first available role if not on a staff page
            currentRole = filteredRoles.length > 0 ? filteredRoles[0].id : "";
          }
          
          setCurrentUserRole(currentRole);
          
          // Check onboarding status
          if (roles.includes("Content Curator")) {
            if (userData.passwordChanged !== true) {
              setShowPasswordChangeModal(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user roles:', error);
      }
    };
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserRoles(user);
      } else {
        // Reset state when user logs out
        setUserRoles([]);
        setUserData(null);
        setAvailableRoles([]);
        setCurrentUserRole("");
        retryCount = 0; // Reset retry count for next login
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.cc-btn') && !event.target.closest('.cc-sidebar-role-dropdown')) {
        setShowRoleDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  
  /* =========================
     Render Main Panel
  ========================= */
  const renderPanel = () => {
    switch (activePanel) {
      case "dashboard":
        return <ContentDashboard onCreateNew={handleCreateNew} onEditDraft={handleEditDraft} />;

      case "library":
        return <ContentLibrary onEditDraft={handleEditDraft} onCreateNew={() => handleCreateNew(false)} />;

      case "announcements":
        return <ContentAnnouncements onEditDraft={handleEditDraft} onCreateNew={() => handleCreateNew(true)} />;

      case "profile":
        return <ContentProfile />;

      case "add-content":
        return <ContentDashboard showAddContent={true} onBack={handleBack} editingDraft={editingDraft} isAnnouncementMode={isAnnouncementMode} />;

      default:
        return <ContentDashboard onCreateNew={handleCreateNew} />;
    }
  };

  return (
    <div className="cc-layout">
      {/* ── Custom Tooltip for Collapsed Sidebar ── */}
      {tooltip.visible && isDesktop && sidebarCollapsed && (
        <div
          className="cc-custom-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* ── Mobile backdrop ── */}
      {isMobileDrawerOpen && (
        <div
          className="cc-drawer-backdrop visible"
          onClick={() => setIsMobileDrawerOpen(false)}
        />
      )}

      {/* ================= Sidebar ================= */}
      <aside className={`cc-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isMobileDrawerOpen ? 'drawer-open' : ''}`}>
        {/* Mobile sidebar header (compact navbar-style branding) */}
        <div className="cc-sidebar-mobile-header">
          <div className="cc-sidebar-mobile-logos">
            <img src={logoName} alt="AuraVit" />
            <span className="cc-sidebar-logo-sep">|</span>
            <img src={cfsiLogo} alt="CFSI" />
          </div>
          <button
            className="cc-drawer-close"
            onClick={() => setIsMobileDrawerOpen(false)}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>

        {/* Desktop brand section */}
        <div 
          className="cc-brand"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          <img
            src={sidebarCollapsed ? auravitIcon : logoName}
            alt="AURAVIT"
            className="cc-sidebar-logo"
          />
          {!sidebarCollapsed && (
            <img
              src={cfsiLogo}
              alt="CFSI"
              className="cc-cfsi-logo"
            />
          )}
        </div>

        <div className="cc-sidebar-content">
          <nav className="cc-nav">
          <button
            className={activePanel === "dashboard" ? "active" : ""}
            onClick={() => { setActivePanel("dashboard"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Dashboard")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Dashboard"
          >
            <FiGrid /> <span>Dashboard</span>
          </button>

          <button
            className={activePanel === "library" ? "active" : ""}
            onClick={() => { setActivePanel("library"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Content Library")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Content Library"
          >
            <FiBookOpen /> <span>Content Library</span>
          </button>

          <button
            className={activePanel === "announcements" ? "active" : ""}
            onClick={() => { setActivePanel("announcements"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Announcements & Events")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Announcements & Events"
          >
            <FiBell /> <span>Announcements & Events</span>
          </button>

          <button
            className={activePanel === "profile" ? "active" : ""}
            onClick={() => { setActivePanel("profile"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Profile")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Profile"
          >
            <FiUser /> <span>Profile</span>
          </button>

          <button
            className="cc-logout"
            onClick={() => setIsLogoutModalOpen(true)}
            onMouseEnter={(e) => handleMouseEnter(e, "Logout")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Logout"
          >
            <FiLogOut /> <span>Logout</span>
          </button>
        </nav>
        </div>

        {/* ================= Sidebar Bottom Actions ================= */}
        <div className="cc-sidebar-bottom">
          <div className="cc-sidebar-bottom-row">
            {/* Switch Account Button */}
            <div className="cc-sidebar-switch">
              <button
                ref={switchButtonRef}
                className="cc-sidebar-switch-btn"
                onClick={handleDropdownToggle}
                onMouseEnter={(e) => handleMouseEnter(e, "Switch Account")}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                aria-label="Switch Account"
              >
                <HiOutlineSwitchVertical />
                <span>Switch Account</span>
                <FiChevronDown className={`cc-chevron ${showRoleDropdown ? 'open' : ''}`} />
              </button>

              {showRoleDropdown && (
                <div 
                  className="cc-sidebar-role-dropdown"
                  style={sidebarCollapsed && isDesktop ? {
                    position: 'fixed',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`
                  } : {}}
                >
                  {availableRoles.length > 0 ? (
                    availableRoles.map((role) => (
                      <button
                        key={role.id}
                        className={`cc-sidebar-role-option ${role.id === currentUserRole ? "active" : ""}`}
                        onClick={() => handleRoleSwitch(role)}
                      >
                        {role.name}
                        {role.id === currentUserRole && <span className="cc-current-indicator">✓</span>}
                      </button>
                    ))
                  ) : (
                    <div className="cc-sidebar-role-option" style={{ color: '#999', cursor: 'default' }}>
                      No roles available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* ── Mobile top header ── */}
      <header className="cc-mobile-header">
        <div className="cc-mobile-header-logos">
          <img src={logoName} alt="AuraVit" />
          <span className="cc-logo-sep">|</span>
          <img src={cfsiLogo} alt="CFSI" />
        </div>
        <button
          className="cc-mobile-hamburger"
          onClick={() => setIsMobileDrawerOpen(true)}
          aria-label="Open menu"
        >
          <FiMenu />
        </button>
      </header>

      {/* ================= Main ================= */}
      {(activePanel === "library" || activePanel === "announcements") ? (
        <section className="cc-main-full"><div className="cc-workspace-no-padding">{renderPanel()}</div></section>
      ) : activePanel === "profile" ? (
        <section className="cc-main-full"><div className="cc-workspace-no-padding">{renderPanel()}</div></section>
      ) : (
        <section className="cc-main">
          <div className="cc-workspace">{renderPanel()}</div>
        </section>
      )}

      <StaffLogoutModal 
        isOpen={isLogoutModalOpen} 
        onClose={() => setIsLogoutModalOpen(false)} 
      />

      {/* Mandatory Password Change Modal */}
      {showPasswordChangeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 65px rgba(15, 23, 42, 0.45)',
            animation: 'scpModalFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <StaffChangePassword userData={userData} isMandatory={true} onPasswordVerificationRequired={handlePasswordVerificationRequired} />
          </div>
        </div>
      )}

      {/* Password Verification Modal */}
      <StaffChangeVerificationModal
        isOpen={showPasswordVerificationModal}
        onClose={() => setShowPasswordVerificationModal(false)}
        flow="password-change"
        pendingPasswordChange={pendingPasswordChange}
        onSuccess={handlePasswordVerificationSuccess}
        onCancel={handlePasswordVerificationCancel}
      />
    </div>
  );
};

export default ContentCurator;
